import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import DropdownSelect from "components/DropdownSelect";
import Avatar from "components/FileAvatar";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDGroupedSelect from "components/MDGroupedSelect";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import MSelect from "components/MSelect";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import branchesService from "services/branches-service";
import categoriesService from "services/categories-service";
import positionsService from "services/positions-service";
import regionsService from "services/regions-service";
import themeService from "services/theme-service";
import usersService from "services/users-service";
import useWindowDimensions from "services/useWindowDimensions";
import useObjectState from "utils/useObjectState";
import useOpen from "utils/useOpen";
import ContactDialog from "./ContactDialog";

function Branch({ action, setRoute, permissions }) {
  const { label } = useLanguage();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const open = useOpen({
    dialog: null,
  });

  const [branch, setBranch] = useState(null);
  const [size, setSize] = useState("small");
  const [savedBranch, setSavedBranch] = useState({});
  const [loading, changeLoading] = useObjectState({
    fetch: false,
    submit: false,
  });
  const [options, setOptions] = useState({
    branchGroups: [],
    categories: [],
    positions: [],
    colorPalettes: [],
    accountManagers: [],
    regions: [],
  });

  useWindowDimensions(({ width, height }) => {
    if (width < 450) {
      setSize({ description: "minuscule", xs: 12 });
    } else if (width < 600) {
      setSize({ description: "small", xs: 6 });
    } else if (width < 900) {
      setSize({ description: "medium", xs: 4 });
    } else {
      setSize({ description: "large", xs: 3 });
    }
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    fetch(source.token);
    return () => {
      source.cancel("Component unmounted");
    };
  }, [action]);

  useEffect(() => {
    if (savedBranch && setRoute) {
      setRoute(["branches", savedBranch.name]);
    }
  }, [savedBranch]);

  function resolve(value, onComplete) {
    const [response, data] = value;
    if (response.ok) {
      onComplete(data);
    }
  }

  async function fetch(cancelToken) {
    if (loading.fetch) return;
    changeLoading("fetch", true);

    const fetchSequence = [
      branchesService.getBranchGroups(null, cancelToken),
      categoriesService.getCategories(null, cancelToken),
      positionsService.getPositions(null, cancelToken),
      themeService.getTheme(null, cancelToken),
      regionsService.getRegions(null, cancelToken),
      usersService.getUsers({ view: "minimal" }),
    ];

    if (action == "edit") {
      fetchSequence.push(branchesService.getBranch(id, cancelToken));
    }

    Promise.all(fetchSequence).then((values) => {
      let branchGroup,
        branchGroupValue,
        category,
        colorPalette,
        region,
        accountManager;

      resolve(values[0], (data) => {
        setOptions((options) => ({ ...options, branchGroups: [...data] }));
        if (!searchParams?.get("branchGroup")) {
          branchGroup = data[0]?.id;
        } else {
          branchGroup = searchParams.get("branchGroup");
        }
        branchGroupValue = data.find((v) => v.id == branchGroup);
      });

      resolve(values[1], (data) => {
        setOptions((options) => ({ ...options, categories: [...data] }));
        category = data[0]?.subcategories[0]?.id;
      });

      resolve(values[2], (data) => {
        setOptions((options) => ({ ...options, positions: [...data] }));
      });

      resolve(values[3], (data) => {
        setOptions((options) => ({ ...options, colorPalettes: [...data] }));
        colorPalette = data[0]?.id;
      });

      resolve(values[4], (data) => {
        setOptions((options) => ({ ...options, regions: [...data] }));
        region = data[0]?.id;
      });

      resolve(values[5], (data) => {
        setOptions((options) => ({ ...options, accountManagers: [...data] }));
        accountManager = data[0]?.id;
      });

      if (action == "edit") {
        resolve(values[6], (data) => {
          setBranch({
            ...data,
          });
          setSavedBranch({ ...data });
        });
      } else {
        setBranch({
          name: "",
          trademark: "",
          subcategoryId: category,
          colorPaletteId: branchGroupValue.colorPaletteId,
          logo: branchGroupValue.logo,
          email: branchGroupValue.email,
          phone: branchGroupValue.phone,
          address: "",
          branchGroupId: branchGroup,
          contacts: [],
          regionId: region,
          accountManagerId: branchGroupValue.accountManagerId,
        });
      }
    });

    changeLoading("fetch", false);
  }

  function handleBranchGroupChange(value) {
    setBranch({ ...branch, branchGroupId: value });
  }

  function handleNameChange(e) {
    setBranch({ ...branch, name: e.target.value });
  }

  function handleTrademarkChange(e) {
    setBranch({ ...branch, trademark: e.target.value });
  }

  const handleLogoChange = (file) => {
    setBranch({ ...branch, logo: file ? file : "reset" });
  };

  function handleEmailChange(e) {
    setBranch({ ...branch, email: e.target.value });
  }

  function handlePhoneChange(e) {
    setBranch({ ...branch, phone: e.target.value });
  }

  function handleAddressChange(e) {
    setBranch({ ...branch, address: e.target.value });
  }

  function handleRegionChange(value) {
    setBranch({ ...branch, regionId: Number(value) });
  }

  function handleColorPaletteChange(value) {
    setBranch({ ...branch, colorPaletteId: value });
  }

  function handleCategoryChange(value) {
    setBranch({ ...branch, subcategoryId: Number(value) });
  }

  function handleContactAdd(newContact) {
    setBranch({
      ...branch,
      contacts: [
        ...branch.contacts,
        {
          ...newContact,
          contactPosition: getContactPositionById(newContact.contactPositionId),
        },
      ],
    });
  }

  function getContactPositionById(id) {
    return options.positions.find((pos) => pos.id == id);
  }

  function handleContactUpdate(newContact) {
    const newContacts = [...branch.contacts];
    newContacts[newContact.index] = {
      ...newContact,
      name: newContact.name,
      email: newContact.email,
      contactPosition: getContactPositionById(newContact.contactPositionId),
      updated: true,
    };
    setBranch({
      ...branch,
      contacts: newContacts,
    });
  }

  function handleContactRemove(contactUserIndex) {
    open.change("dialog", {
      content: label("delete-alert-message"),
      title: label("delete-alert-title"),
      onOk: () => {
        const newContacts = [];
        for (var i = 0; i < branch.contacts.length; i++) {
          if (i != contactUserIndex) {
            newContacts.push(branch.contacts[i]);
          }
        }
        setBranch({ ...branch, contacts: [...newContacts] });
      },
      onCancel: () => {},
    });
  }

  async function handleSubmission(e) {
    changeLoading("submit", true);
    if (action == "new") {
      e.preventDefault();

      const promise = branchesService.createBranch(branch);

      toast.promise(promise, {
        position: "bottom-right",
        loading: label("Creating branch"),
        success: label("Created branch successfully"),
        error: label("Branch was not created"),
        duration: 5,
      });

      const [response, data] = await promise;
      if (response.ok) {
        setBranch(data);
        navigate("/branches/" + data.id);
      } else {
        console.log("Error when creating branch: " + data.error);
      }
    } else if (action == "edit") {
      e.preventDefault();

      const [response, data] = await branchesService.updateBranch(
        branch.id,
        branch
      );
      if (response.ok) {
        const newBranch = {
          ...data,
        };
        setBranch({
          ...newBranch,
        });
        setSavedBranch({ ...newBranch });
      } else {
        console.log("Error when updating branch: " + data.error);
      }
    }
    changeLoading("submit", false);
  }

  const accountManagerOptions = useMemo(() => {
    return (
      options?.accountManagers?.map((accountManager) => {
        return {
          id: accountManager.id,
          label: accountManager.name,
        };
      }) || []
    );
  }, [options?.accountManagers]);

  if (action == "new") {
    if (!permissions.create) {
      return <AccessDenied type="half-page" />;
    }
  } else if (action == "edit") {
    if (!(permissions.get || permissions.update)) {
      return <AccessDenied type="half-page" />;
    }
  }

  return (
    <>
      {branch && (
        <>
          <form onSubmit={handleSubmission}>
            <MDBox display="flex" flexDirection="column" gap={2}>
              <Card>
                <MDBox
                  display="flex"
                  flexDirection={size.description == "large" ? "row" : "column"}
                  gap={2}
                  p={3}
                >
                  <Avatar
                    attachment={branch.logo == "reset" ? null : branch.logo}
                    setAttachment={handleLogoChange}
                    readOnly={!(permissions?.update || permissions?.create)}
                  />
                  <Grid container spacing={1}>
                    <Grid item xs={size.xs}>
                      <MDInput
                        id="name-input"
                        required
                        fullWidth
                        label={label("name-singular")}
                        value={branch.name}
                        onChange={handleNameChange}
                        readOnly={!(permissions?.update || permissions?.create)}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MDGroupedSelect
                        id="category-select"
                        required
                        fullWidth
                        label={label("category-singular")}
                        value={branch.subcategoryId}
                        items={options.categories.map((item) => {
                          return {
                            name: item.description,
                            options: item.subcategories.map((subcategory) => {
                              return {
                                name: subcategory.description,
                                value: subcategory.id,
                              };
                            }),
                          };
                        })}
                        onChange={handleCategoryChange}
                        readOnly={!(permissions?.update || permissions?.create)}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MDInput
                        id="trademark-input"
                        fullWidth
                        label={label("trademark-singular")}
                        value={branch.trademark}
                        onChange={handleTrademarkChange}
                        readOnly={!(permissions?.update || permissions?.create)}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      {permissions?.update || permissions?.create ? (
                        <MDSelect
                          fullWidth
                          id="color palette-select"
                          required
                          value={branch.colorPaletteId}
                          label={label("color-palette-singular")}
                          items={options.colorPalettes.map((cp) => {
                            return {
                              value: cp.id,
                              render: (
                                <MDBox
                                  display="flex"
                                  flexDirection="row"
                                  justifyContent="flex-start"
                                  alignItems="center"
                                  sx={{ width: "100%", gap: ".1rem" }}
                                >
                                  <MDBox
                                    sx={{
                                      flex: 1,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontSize: ".8rem",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {cp.description}
                                  </MDBox>
                                  <MDBox
                                    display="flex"
                                    flexDirection="row"
                                    sx={{ flex: 1 }}
                                  >
                                    <MDBox
                                      sx={{
                                        height: 24,
                                        flex: 3,
                                        backgroundColor: cp.color1,
                                      }}
                                    ></MDBox>
                                    <MDBox
                                      sx={{
                                        flex: 2,
                                        height: 24,
                                        backgroundColor: cp.color2,
                                      }}
                                    ></MDBox>
                                    <MDBox
                                      sx={{
                                        flex: 1,
                                        height: 24,
                                        backgroundColor: cp.color3,
                                      }}
                                    ></MDBox>
                                  </MDBox>
                                </MDBox>
                              ),
                            };
                          })}
                          onChange={handleColorPaletteChange}
                        />
                      ) : (
                        (() => {
                          const colorPalette = options.colorPalettes.find(
                            (cp) => cp.id == branch.colorPaletteId
                          );
                          return (
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              sx={{ width: "100%" }}
                            >
                              <MDBox
                                sx={{
                                  height: 24,
                                  flex: 3,
                                  backgroundColor: colorPalette.color1,
                                }}
                              ></MDBox>
                              <MDBox
                                sx={{
                                  flex: 2,
                                  height: 24,
                                  backgroundColor: colorPalette.color2,
                                }}
                              ></MDBox>
                              <MDBox
                                sx={{
                                  flex: 1,
                                  height: 24,
                                  backgroundColor: colorPalette.color3,
                                }}
                              ></MDBox>
                            </MDBox>
                          );
                        })()
                      )}
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MDInput
                        id="email-input"
                        fullWidth
                        required
                        label={label("email-singular")}
                        value={branch.email}
                        readOnly={!(permissions?.update || permissions?.create)}
                        onChange={handleEmailChange}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MDInput
                        id="phone-input"
                        fullWidth
                        required
                        label={label("phone-singular")}
                        value={branch.phone}
                        readOnly={!(permissions?.update || permissions?.create)}
                        onChange={handlePhoneChange}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MDInput
                        id="address-input"
                        fullWidth
                        required
                        label={label("address-singular")}
                        value={branch.address}
                        readOnly={!(permissions?.update || permissions?.create)}
                        onChange={handleAddressChange}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MSelect
                        id="region-select"
                        fullWidth
                        required
                        label={label("region-singular")}
                        value={branch.regionId}
                        readOnly={!(permissions?.update || permissions?.create)}
                        items={options.regions.map((item) => {
                          return {
                            value: item.id,
                            reference: item.id,
                            render: item.description,
                          };
                        })}
                        onChange={handleRegionChange}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <MSelect
                        id="group-select"
                        fullWidth
                        required
                        label={label("branch-group-singular")}
                        value={branch.branchGroupId}
                        readOnly={!(permissions?.update || permissions?.create)}
                        items={options.branchGroups.map((item) => {
                          return {
                            value: item.id,
                            reference: item.id,
                            render: item.name,
                          };
                        })}
                        onChange={handleBranchGroupChange}
                      />
                    </Grid>

                    <Grid item xs={size.xs}>
                      <DropdownSelect
                        id="account manager-select"
                        label={label("account-manager-singular")}
                        initialValue={
                          branch.accountManagerId == 0 ||
                          !branch.accountManagerId
                            ? ""
                            : branch.accountManagerId
                        }
                        onChange={(newValue) => {
                          setBranch({
                            ...branch,
                            accountManagerId: newValue?.id,
                          });
                        }}
                        items={accountManagerOptions}
                        required={true}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
              <Contacts
                getContactPositionById={getContactPositionById}
                options={options}
                permissions={permissions}
                handleContactAdd={handleContactAdd}
                handleContactUpdate={handleContactUpdate}
                branch={branch}
                handleContactRemove={handleContactRemove}
              />
              {(permissions?.update || permissions?.create) && (
                <LoadingButton
                  loading={loading.submit}
                  type="submit"
                  color="info"
                  variant="gradient"
                  disabled={
                    loading.submit ||
                    (action == "edit" &&
                      JSON.stringify(savedBranch) === JSON.stringify(branch))
                  }
                  sx={{ height: "20px" }}
                >
                  {action == "edit"
                    ? label("save-button")
                    : label("create-button")}
                </LoadingButton>
              )}
            </MDBox>
          </form>
          <AlertDialog
            open={!!open?.get("dialog")}
            setOpen={(v) => open.change("dialog", v)}
            {...(open?.get("dialog") || {
              content: "",
              title: "",
              onOk: () => {},
              onCancel: () => {},
            })}
          />
        </>
      )}
    </>
  );
}

function Contacts({
  permissions,
  handleContactAdd,
  handleContactUpdate,
  branch,
  handleContactRemove,
  options,
  getContactPositionById,
}) {
  const open = useOpen({ dialog: false });
  const { label } = useLanguage();

  return (
    <>
      <ContactDialog
        onSubmit={async (newContact) => {
          if ((open.get("dialog")?.action || "create") == "create") {
            handleContactAdd(newContact);
          } else {
            handleContactUpdate(newContact);
          }
          return { success: true };
        }}
        open={!!open.get("dialog")}
        onClose={() => {
          open.change("dialog", false);
        }}
        action={open.get("dialog")?.action || "create"}
        data={{
          positions: options.positions,
          contact: open.get("dialog")?.contact,
          branch,
        }}
      />
      <Card>
        <MDBox display="flex" flexDirection="column">
          <MDBox
            p={3}
            pb={0}
            display="flex"
            flexDirection="row"
            gap={2}
            sx={{
              alignItems: "start",
              justifyContent: "space-between",
            }}
          >
            <MDTypography variant="h6">{label("contact-plural")}</MDTypography>
            {permissions?.update && (
              <MDButton
                size="small"
                variant="gradient"
                color="info"
                onClick={() => {
                  open.change("dialog", { action: "create", contact: null });
                }}
              >
                <Icon sx={{ fontWeight: "bold" }}>add</Icon>&nbsp;
                {label("add-new-contact-button")}
              </MDButton>
            )}
          </MDBox>

          <MDBox sx={{ width: "100%" }}>
            <DataTable
              table={{
                columns: [
                  {
                    Header: label("name-singular"),
                    accessor: "name",
                    align: "left",
                  },
                  {
                    Header: label("email-singular"),
                    accessor: "email",
                    align: "left",
                  },
                  {
                    Header: label("position-singular"),
                    accessor: "position",
                    align: "left",
                  },
                  {
                    Header: "",
                    accessor: "actions",
                    align: "right",
                  },
                ],
                rows:
                  !branch.contacts || branch.contacts.length == 0
                    ? [{ user: label("no-contacts-for-branch") }]
                    : branch.contacts.map((contact, contactIndex) => {
                        return {
                          name: contact.name,
                          email: contact.email,
                          position:
                            getContactPositionById(contact.contactPositionId)
                              ?.name || "None",
                          actions: (
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              justifyContent="end"
                            >
                              {permissions?.update && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    open.change("dialog", {
                                      action: "edit",
                                      contact: {
                                        ...contact,
                                        index: contactIndex,
                                        contactPositionId:
                                          contact.contactPosition.id,
                                      },
                                    });
                                  }}
                                >
                                  <Icon>edit</Icon>
                                </IconButton>
                              )}
                              {permissions?.update && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    handleContactRemove(contactIndex);
                                  }}
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              )}
                            </MDBox>
                          ),
                        };
                      }),
              }}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder
              entriesPerPage={false}
            />
          </MDBox>
        </MDBox>
      </Card>
    </>
  );
}

export default Branch;

// import { useEffect, useMemo, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";

// // @mui material components
// import Card from "@mui/material/Card";
// import Icon from "@mui/material/Icon";
// import IconButton from "@mui/material/IconButton";

// // Material Dashboard 2 React components
// import { Avatar as MuiAvatar } from "@mui/material";
// import Avatar from "components/FileAvatar";
// import MDBox from "components/MDBox";
// import MDButton from "components/MDButton";
// import MDGroupedSelect from "components/MDGroupedSelect";
// import MDInput from "components/MDInput";
// import MDSelect from "components/MDSelect";
// import MDTypography from "components/MDTypography";
// import MSelect from "components/MSelect";

// import {
//   CreateBranchGroupDialog,
//   CreateColorPaletteDialog,
//   CreateSubcategoryDialog,
// } from "components/Dialogs";

// import Grid from "@mui/material/Grid";

// // Material Dashboard 2 React examples
// import DataTable from "examples/Tables/DataTable";

// import AlertDialog from "components/AlertDialog";

// import axios from "axios";
// import AccessDenied from "components/AccessDenied";
// import DropdownSelect from "components/DropdownSelect";
// import LoadingButton from "components/LoadingButton";
// import UsersAutocomplete from "components/UsersAutocomplete";
// import toast from "react-hot-toast";
// import branchesService from "services/branches-service";
// import categoriesService from "services/categories-service";
// import positionsService from "services/positions-service";
// import regionsService from "services/regions-service";
// import themeService from "services/theme-service";
// import usersService from "services/users-service";
// import useWindowDimensions from "services/useWindowDimensions";
// import ContactDialog from "./ContactDialog";
// import { useLanguage } from "contexts/language.context";

// function Branch({ action, setRoute, permissions }) {
//   const { label } = useLanguage();
//   const [branch, setBranch] = useState(null);

//   const [open, setOpen] = useState(false);
//   const [dialog, setDialog] = useState({
//     content: "",
//     title: "",
//     onOk: () => {},
//     onCancel: () => {},
//   });

//   const [savedBranch, setSavedBranch] = useState({});

//   const [branchGroups, setBranchGroups] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [positions, setPositions] = useState([]);
//   const [colorPalettes, setColorPalettes] = useState([]);
//   const [accountManagers, setAccountManagers] = useState([]);
//   const [regions, setRegions] = useState([]);
//   const [logoFile, setLogoFile] = useState(null);

//   useWindowDimensions(({ width, height }) => {
//     if (width < 450) {
//       setSize({ description: "minuscule", xs: 12 });
//     } else if (width < 600) {
//       setSize({ description: "small", xs: 6 });
//     } else if (width < 900) {
//       setSize({ description: "medium", xs: 4 });
//     } else {
//       setSize({ description: "large", xs: 3 });
//     }
//   });

//   const [size, setSize] = useState("small");

//   const [createBranchGroupOpen, setCreateBranchGroupOpen] = useState(null);
//   const [createContactPositionOpen, setCreateContactPositionOpen] =
//     useState(null);
//   const [createSubcategoryOpen, setCreateSubcategoryOpen] = useState(null);
//   const [createColorPaletteOpen, setCreateColorPaletteOpen] = useState(null);

//   const [contactDialogOpen, setContactDialogOpen] = useState(false);
//   const [contactDialogAction, setContactDialogAction] = useState("create");
//   const [currentContact, setCurrentContact] = useState(null);

//   const [loadingButton, setLoadingButton] = useState(false);

//   const { id } = useParams();
//   const location = useLocation();
//   const query = new URLSearchParams(location.search);

//   const [value, setValue] = useState(null);
//   const [fetching, setFetching] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const source = axios.CancelToken.source();
//     fetch(source.token);
//     return () => {
//       source.cancel("Component unmounted");
//     };
//   }, [action]);

//   useEffect(() => {
//     if (savedBranch && setRoute) {
//       setRoute(["branches", savedBranch.name]);
//     }
//   }, [savedBranch]);

//   async function fetch(cancelToken) {
//     if (fetching) return;
//     setFetching(true);

//     const fetchSequence = [
//       branchesService.getBranchGroups(null, cancelToken),
//       categoriesService.getCategories(null, cancelToken),
//       positionsService.getPositions(null, cancelToken),
//       themeService.getTheme(null, cancelToken),
//       regionsService.getRegions(null, cancelToken),
//       usersService.getUsers(null),
//     ];

//     if (action == "edit") {
//       fetchSequence.push(branchesService.getBranch(id, cancelToken));
//     }

//     Promise.all(fetchSequence).then((values) => {
//       let branchGroup,
//         branchGroupValue,
//         category,
//         colorPalette,
//         region,
//         accountManager;
//       let [response, data] = values[0];
//       if (response.ok) {
//         setBranchGroups(data);
//         if (!query?.get("branchGroup")) {
//           branchGroup = data[0]?.id;
//         } else {
//           branchGroup = query.get("branchGroup");
//         }
//         branchGroupValue = data.find((v) => v.id == branchGroup);
//       } else {
//         console.log("Error while fetching branch groups: " + data.error);
//       }

//       [response, data] = values[1];
//       if (response.ok) {
//         setCategories(data);
//         category = data[0]?.subcategories[0]?.id; // data[0]?.id;
//       } else {
//         console.log("Error while fetching categories: " + data.error);
//       }

//       [response, data] = values[2];
//       if (response.ok) {
//         setPositions(data);
//       } else {
//         console.log("Error while fetching positions: " + data.error);
//       }

//       [response, data] = values[3];
//       if (response.ok) {
//         setColorPalettes(data);
//         colorPalette = data[0]?.id;
//       } else {
//         console.log("Error while fetching theme: " + data.error);
//       }

//       [response, data] = values[4];
//       if (response.ok) {
//         setRegions(data);
//         region = data[0]?.id;
//       } else {
//         console.log("Error while fetching regions: " + data.error);
//       }

//       [response, data] = values[5];
//       if (response.ok) {
//         setAccountManagers(data);
//         accountManager = data[0]?.id;
//       } else {
//         console.log("Error while fetching regions: " + data.error);
//       }

//       if (action == "edit") {
//         [response, data] = values[6];
//         if (response.ok) {
//           const newBranch = getNewObject({
//             ...data,
//           });
//           for (var i = 0; i < newBranch.contacts.length; i++) {
//             newBranch.contacts[i].user = {
//               name: newBranch.contacts[i].name,
//               email: newBranch.contacts[i].email,
//               profileImage: newBranch.contacts[i].profileImage,
//             };
//           }
//           setBranch(
//             getNewObject({
//               ...newBranch,
//             })
//           );
//           setSavedBranch(getNewObject({ ...newBranch }));
//         } else {
//           console.log("Error while fetching branch: " + data.error);
//         }
//       } else {
//         setBranch({
//           name: "",
//           trademark: "",
//           subcategoryId: category,
//           colorPaletteId: branchGroupValue.colorPaletteId,
//           logo: branchGroupValue.logo,
//           email: branchGroupValue.email,
//           phone: branchGroupValue.phone,
//           address: "",
//           branchGroupId: branchGroup,
//           contacts: [],
//           regionId: region,
//           accountManagerId: branchGroupValue.accountManagerId,
//         });
//       }
//     });

//     setFetching(false);
//   }

//   function getNewObject(object) {
//     return { ...object };
//     //return JSON.parse(JSON.stringify(object));
//   }

//   function handleAccountManagerChange(value) {
//     setBranch(getNewObject({ ...branch, accountManagerId: value }));
//   }

//   function handleBranchGroupChange(value) {
//     setBranch(getNewObject({ ...branch, branchGroupId: value }));
//   }

//   function handleNameChange(e) {
//     setBranch(getNewObject({ ...branch, name: e.target.value }));
//   }

//   function handleTrademarkChange(e) {
//     setBranch(getNewObject({ ...branch, trademark: e.target.value }));
//   }

//   const handleLogoChange = (file) => {
//     setBranch(getNewObject({ ...branch, logo: file ? file : "reset" }));
//   };

//   function handleEmailChange(e) {
//     setBranch(getNewObject({ ...branch, email: e.target.value }));
//   }

//   function handlePhoneChange(e) {
//     setBranch(getNewObject({ ...branch, phone: e.target.value }));
//   }

//   function handleAddressChange(e) {
//     setBranch(getNewObject({ ...branch, address: e.target.value }));
//   }

//   function handleRegionChange(value) {
//     setBranch(getNewObject({ ...branch, regionId: Number(value) }));
//   }

//   function handleColorPaletteChange(value) {
//     setBranch(getNewObject({ ...branch, colorPaletteId: value }));
//   }

//   function handleCategoryChange(value) {
//     setBranch(getNewObject({ ...branch, subcategoryId: Number(value) }));
//   }

//   function handleContactAdd(newContact) {
//     setBranch(
//       getNewObject({
//         ...branch,
//         contacts: [
//           ...branch.contacts,
//           {
//             user: {
//               name: newContact.user.name,
//               email: newContact.user.email,
//               profileImage: "",
//             },
//             contactPosition: getContactPositionById(
//               newContact.contactPositionId
//             ),
//           },
//         ],
//       })
//     );
//   }

//   function getContactPositionById(id) {
//     for (var i = 0; i < positions.length; i++) {
//       if (positions[i].id == id) {
//         return { ...positions[i] };
//       }
//     }

//     console.log("ERROR: Contact position with id " + id + " was not found.");
//     return null;
//   }

//   function handleContactUpdate(newContact) {
//     const newContacts = [...branch.contacts];
//     newContacts[newContact.index] = {
//       user: {
//         name: newContact.user.name,
//         email: newContact.user.email,
//         profileImage: "",
//       },
//       contactPosition: getContactPositionById(newContact.contactPositionId),
//     };
//     setBranch(
//       getNewObject({
//         ...branch,
//         contacts: newContacts,
//       })
//     );
//   }

//   function handleContactRemove(contactUserIndex) {
//     setOpen(true);
//     setDialog({
//       content: label("delete-alert-message"),
//       title: label("delete-alert-title"),
//       onOk: () => {
//         const newContacts = [];
//         for (var i = 0; i < branch.contacts.length; i++) {
//           if (i != contactUserIndex) {
//             newContacts.push(branch.contacts[i]);
//           }
//         }
//         setBranch(getNewObject({ ...branch, contacts: [...newContacts] }));
//       },
//       onCancel: () => {},
//     });
//   }

//   function handleContactPositionChange(newPositionId, contactIndex) {
//     const newContacts = [...branch.contacts];

//     var newPosition;
//     for (var i = 0; i < positions.length; i++) {
//       if (positions[i].id == newPositionId) {
//         newPosition = { ...positions[i] };
//       }
//     }
//     newContacts[contactIndex].contactPosition = newPosition;

//     setBranch(getNewObject({ ...branch, contacts: newContacts }));
//   }

//   async function handleSubmission(e) {
//     setLoadingButton(true);
//     if (action == "new") {
//       e.preventDefault();

//       const promise = branchesService.createBranch(branch);

//       toast.promise(promise, {
//         position: "bottom-right",
//         loading: label("Creating branch"),
//         success: label("Created branch successfully"),
//         error: label("Branch was not created"),
//         duration: 5,
//       });

//       const [response, data] = await promise;
//       if (response.ok) {
//         setBranch(data);
//         navigate("/branches/" + data.id);
//       } else {
//         console.log("Error when creating branch: " + data.error);
//       }
//     } else if (action == "edit") {
//       e.preventDefault();

//       const [response, data] = await branchesService.updateBranch(
//         branch.id,
//         branch
//       );
//       if (response.ok) {
//         const newBranch = getNewObject({
//           ...data,
//         });
//         for (var i = 0; i < newBranch.contacts.length; i++) {
//           newBranch.contacts[i].user = {
//             name: newBranch.contacts[i].name,
//             email: newBranch.contacts[i].email,
//             profileImage: newBranch.contacts[i].profileImage,
//           };
//         }
//         setBranch(
//           getNewObject({
//             ...newBranch,
//           })
//         );
//         setSavedBranch(getNewObject({ ...newBranch }));
//       } else {
//         console.log("Error when updating branch: " + data.error);
//       }
//     }
//     setLoadingButton(false);
//   }

//   const accountManagerOptions = useMemo(() => {
//     return (
//       accountManagers?.map((accountManager) => {
//         return {
//           id: accountManager.id,
//           label: accountManager.name,
//         };
//       }) || []
//     );
//   }, [accountManagers]);

//   if (action == "new") {
//     if (!permissions.create) {
//       return <AccessDenied type="half-page" />;
//     }
//   } else if (action == "edit") {
//     if (!(permissions.get || permissions.update)) {
//       return <AccessDenied type="half-page" />;
//     }
//   }

//   return (
//     <>
//       {branch && (
//         <>
//           <form onSubmit={handleSubmission}>
//             <MDBox display="flex" flexDirection="column" gap={2}>
//               <Card>
//                 <MDBox
//                   display="flex"
//                   flexDirection={size.description == "large" ? "row" : "column"}
//                   gap={2}
//                   p={3}
//                 >
//                   <Avatar
//                     attachment={branch.logo == "reset" ? null : branch.logo}
//                     setAttachment={handleLogoChange}
//                     readOnly={!(permissions?.update || permissions?.create)}
//                   />
//                   <Grid container spacing={1}>
//                     <Grid item xs={size.xs}>
//                       <MDInput
//                         id="name-input"
//                         required
//                         fullWidth
//                         label={label("name-singular")}
//                         value={branch.name}
//                         onChange={handleNameChange}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <CreateSubcategoryDialog
//                         isOpen={createSubcategoryOpen}
//                         categories={categories}
//                         onSubmit={(data) => {
//                           setCategories(data);
//                         }}
//                       />
//                       <MDGroupedSelect
//                         id="category-select"
//                         required
//                         fullWidth
//                         label={label("category-singular")}
//                         value={branch.subcategoryId}
//                         items={categories.map((item) => {
//                           return {
//                             name: item.description,
//                             options: item.subcategories.map((subcategory) => {
//                               return {
//                                 name: subcategory.description,
//                                 value: subcategory.id,
//                               };
//                             }),
//                           };
//                         })}
//                         onChange={handleCategoryChange}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <MDInput
//                         id="trademark-input"
//                         fullWidth
//                         label={label("trademark-singular")}
//                         value={branch.trademark}
//                         onChange={handleTrademarkChange}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <CreateColorPaletteDialog
//                         onSubmit={(data) => {
//                           setColorPalettes(data);
//                         }}
//                         isOpen={createColorPaletteOpen}
//                       />
//                       {permissions?.update || permissions?.create ? (
//                         <MDSelect
//                           fullWidth
//                           id="color palette-select"
//                           required
//                           value={branch.colorPaletteId}
//                           label={label("color-palette-singular")}
//                           items={colorPalettes.map((cp) => {
//                             return {
//                               value: cp.id,
//                               render: (
//                                 <MDBox
//                                   display="flex"
//                                   flexDirection="row"
//                                   justifyContent="flex-start"
//                                   alignItems="center"
//                                   sx={{ width: "100%", gap: ".1rem" }}
//                                 >
//                                   <MDBox
//                                     sx={{
//                                       flex: 1,
//                                       whiteSpace: "nowrap",
//                                       overflow: "hidden",
//                                       textOverflow: "ellipsis",
//                                       fontSize: ".8rem",
//                                       fontWeight: "600",
//                                     }}
//                                   >
//                                     {cp.description}
//                                   </MDBox>
//                                   <MDBox
//                                     display="flex"
//                                     flexDirection="row"
//                                     sx={{ flex: 1 }}
//                                   >
//                                     <MDBox
//                                       sx={{
//                                         height: 24,
//                                         flex: 3,
//                                         backgroundColor: cp.color1,
//                                       }}
//                                     ></MDBox>
//                                     <MDBox
//                                       sx={{
//                                         flex: 2,
//                                         height: 24,
//                                         backgroundColor: cp.color2,
//                                       }}
//                                     ></MDBox>
//                                     <MDBox
//                                       sx={{
//                                         flex: 1,
//                                         height: 24,
//                                         backgroundColor: cp.color3,
//                                       }}
//                                     ></MDBox>
//                                   </MDBox>
//                                 </MDBox>
//                               ),
//                             };
//                           })}
//                           onChange={handleColorPaletteChange}
//                         />
//                       ) : (
//                         (() => {
//                           const colorPalette = colorPalettes.find(
//                             (cp) => cp.id == branch.colorPaletteId
//                           );
//                           return (
//                             <MDBox
//                               display="flex"
//                               flexDirection="row"
//                               sx={{ width: "100%" }}
//                             >
//                               <MDBox
//                                 sx={{
//                                   height: 24,
//                                   flex: 3,
//                                   backgroundColor: colorPalette.color1,
//                                 }}
//                               ></MDBox>
//                               <MDBox
//                                 sx={{
//                                   flex: 2,
//                                   height: 24,
//                                   backgroundColor: colorPalette.color2,
//                                 }}
//                               ></MDBox>
//                               <MDBox
//                                 sx={{
//                                   flex: 1,
//                                   height: 24,
//                                   backgroundColor: colorPalette.color3,
//                                 }}
//                               ></MDBox>
//                             </MDBox>
//                           );
//                         })()
//                       )}
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <MDInput
//                         id="email-input"
//                         fullWidth
//                         required
//                         label={label("email-singular")}
//                         value={branch.email}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                         onChange={handleEmailChange}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <MDInput
//                         id="phone-input"
//                         fullWidth
//                         required
//                         label={label("phone-singular")}
//                         value={branch.phone}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                         onChange={handlePhoneChange}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <MDInput
//                         id="address-input"
//                         fullWidth
//                         required
//                         label={label("address-singular")}
//                         value={branch.address}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                         onChange={handleAddressChange}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <MSelect
//                         id="region-select"
//                         fullWidth
//                         required
//                         label={label("region-singular")}
//                         value={branch.regionId}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                         items={regions.map((item) => {
//                           return {
//                             value: item.id,
//                             reference: item.id,
//                             render: item.description,
//                           };
//                         })}
//                         onChange={handleRegionChange}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <CreateBranchGroupDialog
//                         isOpen={createBranchGroupOpen}
//                         onSubmit={(data) => {
//                           setBranchGroups(data);
//                         }}
//                       />
//                       <MSelect
//                         id="group-select"
//                         fullWidth
//                         required
//                         label={label("branch-group-singular")}
//                         value={branch.branchGroupId}
//                         readOnly={!(permissions?.update || permissions?.create)}
//                         items={branchGroups.map((item) => {
//                           return {
//                             value: item.id,
//                             reference: item.id,
//                             render: item.name,
//                           };
//                         })}
//                         onChange={handleBranchGroupChange}
//                       />
//                     </Grid>

//                     <Grid item xs={size.xs}>
//                       <DropdownSelect
//                         id="account manager-select"
//                         label={label("account-manager-singular")}
//                         initialValue={
//                           branch.accountManagerId == 0 ||
//                           !branch.accountManagerId
//                             ? ""
//                             : branch.accountManagerId
//                         }
//                         onChange={(newValue) => {
//                           setBranch({
//                             ...branch,
//                             accountManagerId: newValue?.id,
//                           });
//                         }}
//                         items={accountManagerOptions}
//                         required={true}
//                         fullWidth
//                       />
//                     </Grid>
//                   </Grid>
//                 </MDBox>
//               </Card>
//               <Card>
//                 <MDBox p={3} display="flex" flexDirection="column" gap={2}>
//                   <MDBox
//                     display="flex"
//                     flexDirection="row"
//                     gap={2}
//                     sx={{
//                       alignItems: "center",
//                       justifyContent: "space-between",
//                     }}
//                   >
//                     <MDTypography variant="h6">
//                       {label("contact-plural")}
//                     </MDTypography>
//                     {permissions?.update && (
//                       <MDButton
//                         variant="gradient"
//                         color="info"
//                         onClick={() => {
//                           setContactDialogAction("create");
//                           setContactDialogOpen(Math.random());
//                         }}
//                       >
//                         {label("add-new-contact-button")}
//                       </MDButton>
//                     )}
//                   </MDBox>

//                   <MDBox sx={{ width: "100%" }}>
//                     <DataTable
//                       table={{
//                         columns: [
//                           {
//                             Header: label("name-singular"),
//                             accessor: "user",
//                             align: "left",
//                           },
//                           {
//                             Header: label("position-singular"),
//                             accessor: "position",
//                             align: "left",
//                           },
//                           {
//                             Header: "",
//                             accessor: "actions",
//                             align: "right",
//                           },
//                         ],
//                         rows:
//                           !branch.contacts || branch.contacts.length == 0
//                             ? [{ user: label("no-contacts-for-branch") }]
//                             : branch.contacts.map((contact, contactIndex) => {
//                                 return {
//                                   user: (
//                                     <User
//                                       image={null}
//                                       name={contact.user.name}
//                                       email={contact.user.email}
//                                     />
//                                   ),
//                                   position: (
//                                     <>
//                                       <MSelect
//                                         required
//                                         label={label("position-singular")}
//                                         value={contact.contactPosition?.id}
//                                         items={positions.map((item) => {
//                                           return {
//                                             value: item.id,
//                                             reference: item,
//                                             render: item.name,
//                                           };
//                                         })}
//                                         readOnly={
//                                           !(
//                                             permissions?.update ||
//                                             permissions?.create
//                                           )
//                                         }
//                                         onChange={(newPositionId) => {
//                                           handleContactPositionChange(
//                                             newPositionId,
//                                             contactIndex
//                                           );
//                                         }}
//                                       />
//                                     </>
//                                   ),
//                                   actions: (
//                                     <MDBox display="flex" flexDirection="row">
//                                       {permissions?.update && (
//                                         <IconButton
//                                           size="small"
//                                           onClick={() => {
//                                             setContactDialogAction("edit");
//                                             setContactDialogOpen(Math.random());
//                                             setCurrentContact({
//                                               ...contact,
//                                               index: contactIndex,
//                                               contactPositionId:
//                                                 contact.contactPosition.id,
//                                             });
//                                           }}
//                                         >
//                                           <Icon>edit</Icon>
//                                         </IconButton>
//                                       )}
//                                       {permissions?.update && (
//                                         <IconButton
//                                           size="small"
//                                           onClick={() => {
//                                             handleContactRemove(contactIndex);
//                                           }}
//                                         >
//                                           <Icon>delete</Icon>
//                                         </IconButton>
//                                       )}
//                                     </MDBox>
//                                   ),
//                                 };
//                               }),
//                       }}
//                       showTotalEntries={false}
//                       isSorted={false}
//                       noEndBorder
//                       entriesPerPage={false}
//                     />
//                   </MDBox>
//                 </MDBox>
//               </Card>
//               {(permissions?.update || permissions?.create) && (
//                 <LoadingButton
//                   loading={loadingButton}
//                   type="submit"
//                   color="info"
//                   variant="gradient"
//                   disabled={
//                     loadingButton ||
//                     (action == "edit" &&
//                       JSON.stringify(savedBranch) === JSON.stringify(branch))
//                   }
//                   sx={{ height: "20px" }}
//                 >
//                   {action == "edit"
//                     ? label("save-button")
//                     : label("create-button")}
//                 </LoadingButton>
//               )}
//             </MDBox>
//           </form>
//           <AlertDialog open={open} setOpen={setOpen} {...dialog} />
//           <ContactDialog
//             isOpen={contactDialogOpen}
//             action={contactDialogAction}
//             data={{ positions: positions, contact: { ...currentContact } }}
//             onSubmit={async (newContact) => {
//               if (contactDialogAction == "create") {
//                 handleContactAdd(newContact);
//               } else {
//                 handleContactUpdate(newContact);
//               }
//               return { success: true };
//             }}
//           />
//         </>
//       )}
//     </>
//   );
// }

// const User = ({ image, name, email }) => (
//   <MDBox display="flex" alignItems="center" lineHeight={1}>
//     <MuiAvatar src={image} name={name} sx={{ width: 32, height: 32 }} />
//     <MDBox ml={2} lineHeight={1}>
//       <MDTypography display="block" variant="button" fontWeight="medium">
//         {name}
//       </MDTypography>
//       <MDTypography variant="caption">{email}</MDTypography>
//     </MDBox>
//   </MDBox>
// );

// export default Branch;
